import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { checkPmay, inr, inrShort } from "@/lib/api";
import { BadgeIndianRupee, CheckCircle2, XCircle, Loader2 } from "lucide-react";

export const PmayChecker = ({ totalCost, totalAreaSqft, onResult }) => {
  const suggestedLoan = Math.round((totalCost || 3000000) * 0.8);
  const suggestedCarpet = totalAreaSqft ? Math.round((totalAreaSqft * 0.75) / 10.764) : 90;

  const [income, setIncome] = useState(900000);
  const [loan, setLoan] = useState(suggestedLoan);
  const [carpet, setCarpet] = useState(suggestedCarpet);
  const [firstHome, setFirstHome] = useState(true);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoan(Math.round((totalCost || 3000000) * 0.8));
  }, [totalCost]);

  const run = useCallback(() => {
    if (!loan || loan <= 0) return;
    setLoading(true);
    checkPmay({
      annual_income: Number(income) || 0,
      loan_amount: Number(loan),
      carpet_area_sqm: Number(carpet) || 0,
      first_home: firstHome,
    })
      .then((d) => {
        setRes(d);
        if (onResult) onResult(d);
      })
      .finally(() => setLoading(false));
  }, [income, loan, carpet, firstHome, onResult]);

  useEffect(() => {
    const t = setTimeout(run, 350);
    return () => clearTimeout(t);
  }, [run]);

  return (
    <Card className="bg-slate-900/70 border-slate-800 rounded-2xl p-6 sm:p-8" data-testid="pmay-checker">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-xl bg-purple-500/15 grid place-items-center">
          <BadgeIndianRupee className="h-5 w-5 text-purple-400" />
        </div>
        <div>
          <h3 className="font-heading text-xl font-bold text-white">PMAY Subsidy Check</h3>
          <p className="text-sm text-slate-400">
            Pradhan Mantri Awas Yojana — Credit Linked Subsidy (CLSS) estimator
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Inputs */}
        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-slate-200 font-semibold">Annual Household Income (₹)</Label>
            <Input
              type="number"
              value={income}
              onChange={(e) => setIncome(Number(e.target.value))}
              data-testid="pmay-income"
              className="bg-slate-950 border-slate-700 h-11 text-white font-mono-data focus-visible:ring-orange-500"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200 font-semibold">Home Loan Amount (₹)</Label>
            <Input
              type="number"
              value={loan}
              onChange={(e) => setLoan(Number(e.target.value))}
              data-testid="pmay-loan"
              className="bg-slate-950 border-slate-700 h-11 text-white font-mono-data focus-visible:ring-orange-500"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200 font-semibold">
              Carpet Area (sq.m) <span className="text-xs font-normal text-slate-500">optional</span>
            </Label>
            <Input
              type="number"
              value={carpet}
              onChange={(e) => setCarpet(Number(e.target.value))}
              data-testid="pmay-carpet"
              className="bg-slate-950 border-slate-700 h-11 text-white font-mono-data focus-visible:ring-orange-500"
            />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-slate-700 bg-slate-950 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-white">First pucca house</p>
              <p className="text-xs text-slate-500">No family member owns a permanent house</p>
            </div>
            <Switch
              checked={firstHome}
              onCheckedChange={setFirstHome}
              data-testid="pmay-firsthome"
              className="data-[state=checked]:bg-orange-500"
            />
          </div>
        </div>

        {/* Result */}
        <div className="flex items-center">
          {loading && !res ? (
            <div className="w-full h-40 grid place-items-center">
              <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
            </div>
          ) : res ? (
            <div className="w-full" data-testid="pmay-result">
              {res.eligible ? (
                <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-6">
                  <div className="flex items-center gap-2 text-emerald-400 font-semibold">
                    <CheckCircle2 className="h-5 w-5" /> Likely Eligible
                  </div>
                  <p className="mt-3 text-xs text-slate-400 uppercase tracking-wide">
                    Estimated Interest Subsidy
                  </p>
                  <p
                    className="font-heading text-4xl font-extrabold text-white font-mono-data mt-1"
                    data-testid="pmay-subsidy"
                  >
                    ₹{inr(res.estimated_subsidy)}
                  </p>
                  <p className="text-sm text-emerald-300 mt-0.5">({inrShort(res.estimated_subsidy)})</p>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-lg bg-slate-950/60 p-3">
                      <p className="text-slate-500 text-xs">Category</p>
                      <p className="text-white font-semibold">{res.category_id?.toUpperCase()}</p>
                    </div>
                    <div className="rounded-lg bg-slate-950/60 p-3">
                      <p className="text-slate-500 text-xs">Subsidy Rate</p>
                      <p className="text-white font-semibold font-mono-data">{res.subsidy_rate}%</p>
                    </div>
                  </div>
                  <p className="mt-4 text-xs text-slate-400 leading-relaxed">{res.reason}</p>
                </div>
              ) : (
                <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-6">
                  <div className="flex items-center gap-2 text-red-400 font-semibold">
                    <XCircle className="h-5 w-5" /> Not Eligible
                  </div>
                  <p className="mt-3 text-sm text-slate-300 leading-relaxed">{res.reason}</p>
                  {res.category && (
                    <p className="mt-2 text-xs text-slate-500">Assessed category: {res.category}</p>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <p className="text-xs text-slate-500 mt-5">
        * Indicative estimate based on CLSS income tiers (EWS/LIG/MIG). Final eligibility & subsidy
        are decided by your lender and the Ministry of Housing & Urban Affairs. Scheme terms may
        change — verify on pmaymis.gov.in.
      </p>
    </Card>
  );
};
